import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const VARIANTS = {
  quiz: {
    key: 'quiz_popup_dismissed',
    heading: 'GET A FREE MARKETING REPORT AND QUOTE',
    intro: 'Tell us about your organisation and receive a personalised marketing plan with an indicative quote.',
    body: 'Answer a few questions about your goals, budget and current situation. We will generate a tailored strategy and pricing breakdown, completely free, with no obligation.',
    cta: 'GET MY FREE REPORT',
    to: '/quiz',
  },
  builder: {
    key: 'wb_popup_dismissed',
    heading: "DON'T LOSE YOUR QUOTE",
    intro: "You're moments away from an instant quote and a live AI preview of your new website.",
    body: 'Finish the quick questionnaire to see your price and a mockup of your site, free, with no obligation. It takes about two minutes.',
    cta: 'FINISH & SEE MY QUOTE',
    to: '/website-builder',
  },
};

const QuizPopup = () => {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const path = location.pathname;

  const excluded = path.startsWith('/admin') || path.startsWith('/quiz') || path.startsWith('/website-builder/')
    || path.startsWith('/courses') || path.startsWith('/academy') || path.startsWith('/certificates');
  const variant = path.startsWith('/website-builder') ? 'builder' : 'quiz';
  const v = VARIANTS[variant];

  useEffect(() => {
    if (excluded) return;
    if (localStorage.getItem(v.key)) return;

    let done = false;
    const trigger = () => { if (!done) { done = true; setShow(true); } };

    // Exit-intent: mouse leaves toward the top of the viewport (desktop).
    const onMouseOut = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener('mouseout', onMouseOut);

    // Time-based fallback for the general quiz variant only.
    let timer;
    if (variant === 'quiz') timer = setTimeout(trigger, 8000);

    return () => { document.removeEventListener('mouseout', onMouseOut); if (timer) clearTimeout(timer); };
  }, [path, excluded, variant, v.key]);

  const dismiss = useCallback(() => {
    setShow(false);
    localStorage.setItem(v.key, 'true');
  }, [v.key]);

  if (!show || excluded) return null;

  return (
    <div data-testid="quiz-popup-overlay" onClick={dismiss} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="quiz-popup" data-variant={variant} style={{ background: '#fff', maxWidth: 480, width: '100%', position: 'relative', overflow: 'hidden', animation: 'fadeInUp 0.4s ease-out' }}>
        <div style={{ background: 'var(--hot-pink)', padding: '32px 32px 24px' }}>
          <button onClick={dismiss} data-testid="quiz-popup-close" style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: '#fff', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} />
          </button>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', color: '#fff', textTransform: 'uppercase', lineHeight: 1.2, marginBottom: 8 }}>
            {v.heading}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '18px', lineHeight: 1.6 }}>
            {v.intro}
          </p>
        </div>
        <div style={{ padding: '24px 32px 32px' }}>
          <p style={{ fontSize: '18px', color: 'var(--body-text)', lineHeight: 1.7, marginBottom: 20 }}>
            {v.body}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link to={v.to} onClick={dismiss} data-testid="quiz-popup-cta" className="btn-pink-filled" style={{ flex: 1, justifyContent: 'center', padding: '9px 14px', fontSize: 12 }}>
              {v.cta} <ArrowRight size={13} />
            </Link>
            <button onClick={dismiss} className="btn-dark-outline" style={{ whiteSpace: 'nowrap', padding: '9px 16px', fontSize: 12 }}>
              NO THANKS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPopup;
